/**
 * Text messages (SMS), provider-agnostic like email. Only BCBAs who opted in (consent recorded
 * in the database) are ever texted, only about their own credentialing, and never with PHI.
 *
 * SMS_PROVIDER: "twilio" | "console" (default: none -> texts are skipped, not queued forever)
 * Twilio needs TWILIO_ACCOUNT_SID, TWILIO_AUTH_TOKEN (secret) and either
 * TWILIO_MESSAGING_SERVICE_SID (recommended, required for A2P 10DLC) or TWILIO_FROM (+1...).
 */
export interface SmsMessage { to: string; body: string }
export interface SmsSender { readonly name: string; send(m: SmsMessage): Promise<void> }

export interface SmsEnv {
  SMS_PROVIDER?: string;
  SMS_REDIRECT_ALL_TO?: string; // testing: deliver every text to this number instead
  TWILIO_ACCOUNT_SID?: string;
  TWILIO_AUTH_TOKEN?: string;
  TWILIO_MESSAGING_SERVICE_SID?: string;
  TWILIO_FROM?: string;
}

type Fetcher = typeof fetch;

/** Normalises a US phone number to E.164 (+1XXXXXXXXXX), or null. Mirrors app.us_e164 in SQL. */
export function usE164(p: string | null | undefined): string | null {
  const d = (p ?? "").replace(/\D/g, "");
  return /^1?[2-9]\d{9}$/.test(d) ? `+1${d.slice(-10)}` : null;
}

export class ConsoleSms implements SmsSender {
  readonly name = "console";
  readonly sent: SmsMessage[] = [];
  async send(m: SmsMessage) {
    this.sent.push(m);
    console.log(`[sms:console] to=${m.to} ${JSON.stringify(m.body)}`);
  }
}

export class TwilioSms implements SmsSender {
  readonly name = "twilio";
  constructor(private readonly cfg: { sid: string; token: string; service?: string; from?: string }, private readonly fetcher: Fetcher = fetch) {}
  async send(m: SmsMessage) {
    const form = new URLSearchParams({ To: m.to, Body: m.body });
    if (this.cfg.service) form.set("MessagingServiceSid", this.cfg.service);
    else form.set("From", this.cfg.from!);
    const res = await this.fetcher(`https://api.twilio.com/2010-04-01/Accounts/${this.cfg.sid}/Messages.json`, {
      method: "POST",
      headers: { authorization: `Basic ${btoa(`${this.cfg.sid}:${this.cfg.token}`)}`, "content-type": "application/x-www-form-urlencoded" },
      body: form,
    });
    if (!res.ok) {
      const j = (await res.json().catch(() => ({}))) as { code?: number; message?: string };
      // 21610 = the recipient replied STOP; Twilio blocks further texts to them.
      throw new Error(`twilio send failed (${res.status}${j.code ? ` / ${j.code}` : ""}): ${j.message ?? ""}`.trim());
    }
  }
}

class RedirectingSms implements SmsSender {
  readonly name: string;
  constructor(private readonly inner: SmsSender, private readonly to: string) { this.name = inner.name; }
  async send(m: SmsMessage) {
    await this.inner.send({ to: this.to, body: `[TEST for ${m.to}] ${m.body}` });
  }
}

/** Returns the configured sender, or null when texting isn't set up (messages are then skipped). */
export function createSmsSender(env: SmsEnv, fetcher: Fetcher = fetch): SmsSender | null {
  let s: SmsSender | null;
  switch (env.SMS_PROVIDER) {
    case undefined: case "": case "none": s = null; break;
    case "console": s = new ConsoleSms(); break;
    case "twilio":
      if (!env.TWILIO_ACCOUNT_SID || !env.TWILIO_AUTH_TOKEN) throw new Error("SMS_PROVIDER is twilio but TWILIO_ACCOUNT_SID / TWILIO_AUTH_TOKEN are not set");
      if (!env.TWILIO_MESSAGING_SERVICE_SID && !env.TWILIO_FROM) throw new Error("Set TWILIO_MESSAGING_SERVICE_SID (or TWILIO_FROM)");
      s = new TwilioSms({ sid: env.TWILIO_ACCOUNT_SID, token: env.TWILIO_AUTH_TOKEN, service: env.TWILIO_MESSAGING_SERVICE_SID, from: env.TWILIO_FROM }, fetcher);
      break;
    default: throw new Error(`Unknown SMS_PROVIDER "${env.SMS_PROVIDER}"`);
  }
  const redirect = usE164(env.SMS_REDIRECT_ALL_TO);
  return s && redirect ? new RedirectingSms(s, redirect) : s;
}

/** Texts are held overnight: only sent 8am-9pm America/New_York. */
export function isQuietHours(now: Date): boolean {
  const h = Number(new Intl.DateTimeFormat("en-US", { timeZone: "America/New_York", hour: "numeric", hourCycle: "h23" }).format(now));
  return h < 8 || h >= 21;
}

/**
 * Verifies Twilio's X-Twilio-Signature: base64(HMAC-SHA1(authToken, url + sorted key+value pairs)).
 * https://www.twilio.com/docs/usage/webhooks/webhooks-security
 */
export async function verifyTwilioSignature(authToken: string, url: string, params: Record<string, string>, signature: string): Promise<boolean> {
  const data = url + Object.keys(params).sort().map((k) => k + params[k]).join("");
  const key = await crypto.subtle.importKey("raw", new TextEncoder().encode(authToken), { name: "HMAC", hash: "SHA-1" }, false, ["sign"]);
  const mac = new Uint8Array(await crypto.subtle.sign("HMAC", key, new TextEncoder().encode(data)));
  const expected = btoa(String.fromCharCode(...mac));
  if (expected.length !== signature.length) return false;
  let diff = 0;
  for (let i = 0; i < expected.length; i++) diff |= expected.charCodeAt(i) ^ signature.charCodeAt(i);
  return diff === 0;
}

/** Standard carrier keywords. */
export function smsKeyword(body: string): "stop" | "start" | "help" | null {
  const w = body.trim().toUpperCase();
  if (["STOP", "STOPALL", "UNSUBSCRIBE", "CANCEL", "END", "QUIT", "OPTOUT", "REVOKE"].includes(w)) return "stop";
  if (["START", "UNSTOP", "YES"].includes(w)) return "start";
  if (["HELP", "INFO"].includes(w)) return "help";
  return null;
}

/** The exact consent wording shown to the BCBA and stored with their opt-in. */
export const SMS_CONSENT_TEXT =
  "I agree to receive automated text reminders about my own credentialing (expiring documents, CAQH re-attestation, missing items) " +
  "at this number. About 1-4 messages a month. Msg & data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of employment.";
